import { useState, useMemo, useCallback, useEffect } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { usePagination } from "../../../../hooks/usePagination";
import { COLLECTIONS } from "../../../../services/collections";
import { collection, query, where, getAggregateFromServer, sum, count } from "firebase/firestore";
import { db } from "../../../../services/firebase";

const todayStr = () => new Date().toISOString().split("T")[0];

export const useSalesSearch = () => {
    const { idTenant } = useTenant();

    // Valores do Input (Controlados pela interface, não causam renderização na tabela imediatamente)
    const [startDate, setStartDate] = useState(todayStr());
    const [endDate, setEndDate] = useState(todayStr());

    // Flag que indica se o usuário já clicou em "Buscar" pelo menos uma vez
    const [hasSearched, setHasSearched] = useState(false);

    // Valores ativados (Engatilhados apenas ao clicar em "Buscar")
    const [appliedStartDate, setAppliedStartDate] = useState(null);
    const [appliedEndDate, setAppliedEndDate] = useState(null);

    const [aggregates, setAggregates] = useState({ totalAmount: 0, count: 0, loading: false });

    // Os filtros que a paginação enxerga são baseados nas datas ATIVADAS, não nas de Input
    const filters = useMemo(() => {
        if (!appliedStartDate || !appliedEndDate) return null;
        return [
            { field: "date", operator: ">=", value: appliedStartDate },
            { field: "date", operator: "<=", value: appliedEndDate }
        ];
    }, [appliedStartDate, appliedEndDate]);

    // Desabilita a paginação enquanto não houver filtros aplicados (skip = true)
    const {
        data: sales,
        loading: paginationLoading,
        hasMore,
        loadMore
    } = usePagination(`tenants/${idTenant}/${COLLECTIONS.SALES}`, idTenant, {
        pageSize: 20,
        orderField: "date",
        orderDirection: "desc",
        filters: filters || [],
        skip: !hasSearched
    });

    const fetchAggregates = useCallback(async () => {
        if (!idTenant || !appliedStartDate || !appliedEndDate) return;
        setAggregates(prev => ({ ...prev, loading: true }));
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SALES),
                where("date", ">=", appliedStartDate),
                where("date", "<=", appliedEndDate)
            );
            const snapshot = await getAggregateFromServer(q, {
                totalSum: sum("totalAmount"),
                totalCount: count()
            });
            setAggregates({ 
                totalAmount: snapshot.data().totalSum || 0, 
                count: snapshot.data().totalCount || 0,
                loading: false 
            });
        } catch (error) {
            console.error("Erro ao buscar totalizadores:", error);
            setAggregates(prev => ({ ...prev, loading: false }));
        }
    }, [idTenant, appliedStartDate, appliedEndDate]);

    const handleSearch = () => {
        // Ao clicar em buscar, sincronizamos as datas do form com as datas ativadas
        // Isso vai re-calcular 'filters' e o hook usePagination fará um fetch automaticamente
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setHasSearched(true);
    };

    // Busca os agregados sempre que as datas ativadas mudarem (e existirem)
    useEffect(() => {
        if (hasSearched && appliedStartDate && appliedEndDate) {
            fetchAggregates();
        }
    }, [hasSearched, appliedStartDate, appliedEndDate, fetchAggregates]);

    return {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        sales: hasSearched ? sales : [],
        loading: paginationLoading,
        hasMore,
        loadMore,
        aggregates,
        handleSearch,
        hasSearched
    };
};
